import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  Typography,
  TablePagination,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import { Edit, Delete, Mail, Markunread, Drafts, AttachFile, InsertDriveFile } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useSortTable from '../../../hooks/useSortTable';

const CorrespondenceTable = ({ correspondences, onEdit, onDelete, onToggleRead, page, onPageChange, pagination, canEdit, canDelete }) => {
  const navigate = useNavigate();
  const { sorted, sortBy, sortDir, handleSort } = useSortTable(correspondences);

  if (!correspondences || correspondences.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Mail sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <p>No hay correspondencias registradas</p>
      </Paper>
    );
  }

  const handleChangePage = (event, newPage) => {
    onPageChange(newPage + 1);
  };

  const getStatusColor = (status) => {
    const colors = {
      registered: 'warning',
      assigned: 'info',
      closed: 'success',
    };
    return colors[status] || 'default';
  };

  const statusBg = { registered: '#FFFDE7', assigned: '#E3F2FD', closed: '#E8F5E9' };

  const SortCell = ({ label, field, sx: extraSx }) => (
    <TableCell onClick={() => handleSort(field)} sx={{ cursor: 'pointer', userSelect: 'none', ...extraSx }}>
      <TableSortLabel active={sortBy === field} direction={sortBy === field ? sortDir : 'asc'}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const getStatusLabel = (status) => {
    const labels = {
      registered: 'Registrada',
      assigned: 'Asignada',
      closed: 'Cerrada',
    };
    return labels[status] || status;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES');
  };

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <SortCell label="Radicado Entrada" field="in_settled" />
              <SortCell label="Radicado Salida" field="out_settled" sx={{ display: { xs: 'none', md: 'table-cell' } }} />
              <SortCell label="Título" field="title" />
              <SortCell label="Destinatario" field="correspondenceUser.name" sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
              <SortCell label="Tipo" field="correspondenceType.name" sx={{ display: { xs: 'none', md: 'table-cell' } }} />
              <SortCell label="Estado" field="status" />
              <SortCell label="Asignado a" field="assignedUser.name" sx={{ display: { xs: 'none', lg: 'table-cell' } }} />
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Hilos</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Docs</TableCell>
              <SortCell label="Fecha" field="createdAt" sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((correspondence) => (
              <TableRow
                key={correspondence.id}
                hover
                onClick={() => navigate(`/correspondences/${correspondence.id}`)}
                sx={{
                  cursor: 'pointer',
                  bgcolor: statusBg[correspondence.status] || 'inherit',
                  ...(!correspondence.isRead && {
                    borderLeft: '3px solid',
                    borderLeftColor: 'primary.main',
                  }),
                }}
              >
                <TableCell>
                  <Chip
                    label={correspondence.in_settled}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  {correspondence.out_settled ? (
                    <Chip
                      label={correspondence.out_settled}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="caption" color="text.disabled">—</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: correspondence.isRead ? 400 : 700 }}
                  >
                    {correspondence.title}
                  </Typography>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  {correspondence.correspondenceUser ? (
                    <Box>
                      <div>{correspondence.correspondenceUser.name}</div>
                      <small style={{ color: 'gray' }}>{correspondence.correspondenceUser.email}</small>
                    </Box>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  {correspondence.correspondenceType?.name || '-'}
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(correspondence.status)}
                    size="small"
                    color={getStatusColor(correspondence.status)}
                  />
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                  {correspondence.assignedUser ? (
                    <Box>
                      <div>{correspondence.assignedUser.name}</div>
                      <small style={{ color: 'gray' }}>{correspondence.assignedUser.email}</small>
                    </Box>
                  ) : (
                    <Chip label="Sin asignar" size="small" variant="outlined" />
                  )}
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <Chip
                    label={correspondence._count?.threads || 0}
                    size="small"
                    color={correspondence._count?.threads > 0 ? 'primary' : 'default'}
                  />
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, maxWidth: 220 }}>
                  {correspondence.documents?.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                      {correspondence.documents.slice(0, 3).map((doc) => (
                        <Tooltip key={Number(doc.id)} title={doc.name || doc.file_original_name}>
                          <Chip
                            icon={<InsertDriveFile sx={{ fontSize: 14 }} />}
                            label={doc.name || doc.file_original_name || `Doc ${doc.id}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', maxWidth: 200, justifyContent: 'flex-start' }}
                          />
                        </Tooltip>
                      ))}
                      {correspondence.documents.length > 3 && (
                        <Typography variant="caption" color="text.secondary">
                          +{correspondence.documents.length - 3} más
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.disabled">—</Typography>
                  )}
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Typography variant="caption" color="text.secondary">
                    {correspondence.createdAt
                      ? new Date(correspondence.createdAt).toLocaleString('es-ES')
                      : '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={correspondence.isRead ? 'Marcar como no leída' : 'Marcar como leída'}>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); onToggleRead(correspondence.id); }}
                    >
                      {correspondence.isRead ? <Drafts sx={{ fontSize: 18 }} /> : <Markunread color="primary" sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </Tooltip>
                  {canEdit && (
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => { e.stopPropagation(); onEdit(correspondence); }}
                        disabled={correspondence.status === 'closed'}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Eliminar">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => { e.stopPropagation(); onDelete(correspondence.id); }}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && (
        <TablePagination
          component="div"
          count={pagination.total || 0}
          page={(page || 1) - 1}
          onPageChange={handleChangePage}
          rowsPerPage={pagination.limit || 10}
          rowsPerPageOptions={[10]}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      )}
    </Paper>
  );
};

export default CorrespondenceTable;
